 

import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import * as XLSX from 'xlsx';

import { ntxAPI } from '@API/axiosNeatex';
import type { APIResponse } from '@store/Shared/shared';
import { useAppSelector } from '@store/hooks';
import { selectReport } from '@store/DynamicSlice/reportTableSlice';
import { useTranslation } from 'react-i18next';

type PagedApiData<T = any> = {
  totalItems?: number;
  totalRows?: number;

  pageIndex?: number;
 
  pageSize?: number;

  items?: T[];
  rows?: T[];

  totalPages?: number | null;
  hasPrevPage?: boolean | number | null;
  hasNextPage?: boolean | number | null;

  [k: string]: any;
};

export type ExportColumn<T = any> = {
  headerAr: string;
  headerEn: string;
  value: (row: T, ctx: { isAr: boolean }) => any;
};

export type ExportFileName =
  | string
  | ((ctx: { isAr: boolean; reportKey: string; endpoint: string; request: Record<string, any> }) => string);

export type ExportSheetName = string | ((ctx: { isAr: boolean; reportKey: string }) => string);

const n0 = (v: any) => (v === null || v === undefined || Number.isNaN(Number(v)) ? 0 : Number(v));

const DEFAULT_EXPORT_PAGE_SIZE = 500;
const MAX_ROWS_PER_SHEET = 900_000;

export interface ReportExportExcelModalProps<T = any> {
  show: boolean;
  onHide: () => void;

  reportKey: string;
  endpoint: string;

  columns: ExportColumn<T>[];

  exportPageSize?: number;
  fileName?: ExportFileName;
  sheetName?: ExportSheetName;

  requestOverride?: Record<string, any>;
  queryParams?: Record<string, any>;

  maxRowsToExport?: number;
}

function resolveName(value: ExportFileName | undefined, ctx: any, fallback: string) {
  if (!value) return fallback;
  return typeof value === 'function' ? value(ctx) : value;
}

function resolveSheet(value: ExportSheetName | undefined, ctx: any, fallback: string) {
  if (!value) return fallback;
  return typeof value === 'function' ? value(ctx) : value;
}

const ReportExportExcelModal = <T,>(props: ReportExportExcelModalProps<T>) => {
  const {
    show,
    onHide,
    reportKey,
    endpoint,
    columns,
    exportPageSize = DEFAULT_EXPORT_PAGE_SIZE,
    fileName,
    sheetName,
    requestOverride,
    queryParams,
    maxRowsToExport
  } = props;

  const { t, i18n } = useTranslation();
  const isAr = i18n.language !== 'en';

  const report = useAppSelector(s => selectReport(s as any, reportKey)) as any;

  const baseRequest = useMemo(() => {
    const req = (report?.request ?? {}) as Record<string, any>;
    const fixed = (report?.fixedRequest ?? {}) as Record<string, any>;
    return {
      ...req,
      ...fixed,
      ...(requestOverride || {}),
      includeTotalRows: req.includeTotalRows ?? true
    };
  }, [report?.request, report?.fixedRequest, requestOverride]);

  const controllerRef = useRef<AbortController | null>(null);
  const runningRef = useRef(false);

  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'canceled' | 'error'>('idle');
  const [progress, setProgress] = useState(0);
  const [pageInfo, setPageInfo] = useState({ page: 0, totalPages: 0 });
  const [rowsInfo, setRowsInfo] = useState({ loaded: 0, total: 0 });
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const resetState = () => {
    setStatus('idle');
    setErrMsg(null);
    setProgress(0);
    setPageInfo({ page: 0, totalPages: 0 });
    setRowsInfo({ loaded: 0, total: 0 });
  };

  useEffect(() => {
    if (show) resetState();
  }, [show]);

  const abortExport = () => {
    try {
      controllerRef.current?.abort();
    } catch (err) {
      console.log(err);
    }
  };

  const safeClose = () => {
    abortExport();
    onHide();
  };

  const postPage = async (pageIndex: number, signal: AbortSignal) => {
    const req = {
      ...baseRequest,
       pageIndex:pageIndex ,
      pageSize: exportPageSize,
      includeTotalRows: true,
      includeTotalItems: true
    };

    const body = { request: req };
// debugger
    const { data } = await ntxAPI.post<APIResponse<PagedApiData<T>>>(endpoint, body, {
      signal,
      params: queryParams
    });

    if (data?.succeeded !== true) {
      throw new Error((data as any)?.message || 'Export fetch failed');
    }

    const d = (data.data ?? {}) as PagedApiData<T>;

    const items = (d.items ?? d.rows ?? []) as T[];

    const totalItems = n0(d.totalItems ?? d.totalRows ?? 0);
    const pIndex = n0(d.pageIndex );
    const pSize = n0(d.pageSize ?? exportPageSize);

    const computedTotalPages = pSize > 0 ? Math.ceil(totalItems / pSize) : 0;
    const totalPages = n0((typeof d.totalPages === 'number' ? d.totalPages : null) ?? computedTotalPages);

    const hasNext = d.hasNextPage ?? (totalPages > 0 ? pIndex < totalPages : items.length === pSize);

    return {
      items,
      pageIndex: pIndex,
      pageSize: pSize,
      totalItems,
      totalPages,
      hasNext: Boolean(hasNext)
    };
  };

  const buildHeader = () => columns.map(c => (isAr ? c.headerAr : c.headerEn));

  const rowToAoa = (r: T) => columns.map(c => c.value(r, { isAr }));

  const startExport = async () => {
    if (runningRef.current) return;

    runningRef.current = true;
    setStatus('running');
    setErrMsg(null);
    setProgress(0);
    setPageInfo({ page: 0, totalPages: 0 });
    setRowsInfo({ loaded: 0, total: 0 });

    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      const wb = XLSX.utils.book_new();

      const ctx = { isAr, reportKey, endpoint, request: baseRequest };

      const baseSheetName = resolveSheet(sheetName, { isAr, reportKey }, 'Export');
      const makeNewSheet = (sheetIndex: number) => {
        const name = sheetIndex === 1 ? baseSheetName : `${baseSheetName} (${sheetIndex})`;
        const ws = XLSX.utils.aoa_to_sheet([buildHeader()]);
        XLSX.utils.book_append_sheet(wb, ws, name);
        return ws;
      };

      let sheetIndex = 1;
      let ws = makeNewSheet(sheetIndex);
      let sheetRowsCount = 1;

      const first = await postPage(1, controller.signal);

      if (maxRowsToExport && first.totalItems > maxRowsToExport) {
        throw new Error(`Export aborted: total rows (${first.totalItems}) exceeds limit (${maxRowsToExport}).`);
      }

      const totalPages = first.totalPages;
      const totalRows = first.totalItems;

      let loaded = 0;

      const firstAoa = first.items.map(rowToAoa);
      if (firstAoa.length) {
        if (sheetRowsCount + firstAoa.length > MAX_ROWS_PER_SHEET) {
          sheetIndex++;
          ws = makeNewSheet(sheetIndex);
          sheetRowsCount = 1;
        }
        XLSX.utils.sheet_add_aoa(ws, firstAoa, { origin: -1 });
        sheetRowsCount += firstAoa.length;
      }
// debugger
      loaded += first.items.length;
      setPageInfo({ page: 1, totalPages });
      setRowsInfo({ loaded, total: totalRows });
      setProgress(totalRows > 0 ? Math.min(99, Math.round((loaded / totalRows) * 100)) : 0);

      let page = 1;
      let hasNext = first.hasNext;

      while (hasNext) {
        page++;

        const r = await postPage(page, controller.signal);

        const aoa = r.items.map(rowToAoa);
        if (aoa.length) {
          if (sheetRowsCount + aoa.length > MAX_ROWS_PER_SHEET) {
            sheetIndex++;
            ws = makeNewSheet(sheetIndex);
            sheetRowsCount = 1;
          }
          XLSX.utils.sheet_add_aoa(ws, aoa, { origin: -1 });
          sheetRowsCount += aoa.length;
        }

        loaded += r.items.length;

        setPageInfo({ page, totalPages: r.totalPages });
        setRowsInfo({ loaded, total: r.totalItems });

        const pct = r.totalItems > 0 ? Math.min(99, Math.round((loaded / r.totalItems) * 100)) : 0;
        setProgress(pct);

        hasNext = r.hasNext;
      }

      const fallbackName = `${reportKey}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const name = resolveName(fileName, ctx, fallbackName);

      XLSX.writeFile(wb, name);

      setProgress(100);
      setStatus('done');
    } catch (e: any) {
      const aborted = controllerRef.current?.signal?.aborted;
      if (aborted) {
        setStatus('canceled');
      } else {
        setStatus('error');
        setErrMsg(e?.message || 'Export failed');
      }
    } finally {
      runningRef.current = false;
      controllerRef.current = null;
    }
  };

  const cancelExport = () => {
    abortExport();
    setStatus('canceled');
  };

  const isRunning = status === 'running';

  const progressVariant =
    status === 'done' ? 'success' : 
    status === 'error' ? 'danger' : 
    status === 'canceled' ? 'warning' : 
    'theme';

  const progressAnimated = isRunning;
  const progressStriped = isRunning;

  return (
    <Modal
      show={show}
      onHide={safeClose}
      backdrop='static'
      keyboard={false}
      centered
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <Modal.Header closeButton={!isRunning}>
        <Modal.Title className='d-flex align-items-center gap-3'>
          <i className="bi bi-file-earmark-excel-fill text-success" style={{ fontSize: '28px' }}></i>
          <div className='d-flex align-items-center gap-2'>
            {isAr ? 'تصدير  Excel' : 'Export to Excel'}
            {status === 'done' && <span className='text-success fs-4'>✅</span>}
            {status === 'error' && <span className='text-danger fs-4'>❌</span>}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Card className='shadow-sm border-0'>
          <Card.Body>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h6 className='mb-0 fw-bold text-theme'>
                {status === 'idle' && (isAr ? 'جاهز للتصدير' : 'Ready to export')}
                {status === 'running' && (isAr ? 'جاري التصدير...' : 'Exporting...')}
                {status === 'done' && (isAr ? 'تم التصدير بنجاح' : 'Export completed successfully')}
                {status === 'canceled' && (isAr ? 'تم إلغاء التصدير' : 'Export canceled')}
                {status === 'error' && (isAr ? 'فشل التصدير' : 'Export failed')}
              </h6>

              {pageInfo.totalPages > 0 && (
                <span className='text-muted small'>
                  {isAr ? 'صفحة' : 'Page'} {pageInfo.page} / {pageInfo.totalPages}
                </span>
              )}
            </div>

            <ProgressBar
              now={progress}
              variant={progressVariant}
              striped={progressStriped}
              animated={progressAnimated}
              label={`${progress}%`}
              className='mb-3'
              style={{ height: '28px' }}
            />

            <div className='d-flex justify-content-between text-muted small'>
              <span>{progress}%</span>
              <span>
                {isAr ? 'تم تحميل' : 'Loaded'} {rowsInfo.loaded.toLocaleString()}{' '}
                {rowsInfo.total > 0 && (
                  <>
                    {isAr ? 'من' : 'of'} {rowsInfo.total.toLocaleString()}
                  </>
                )}
              </span>
            </div>
          </Card.Body>
        </Card>

        {errMsg && (
          <Alert variant='danger' className='mt-3 mb-0'>
            <strong>{isAr ? 'خطأ:' : 'Error:'}</strong> {errMsg}
          </Alert>
        )}

        {status === 'idle' && (
          <Alert variant='info' className='mt-3 mb-0'>
            {isAr
              ? 'اضغط "ابدأ التصدير" لبدء تصدير جميع النتائج حسب الفلاتر الحالية.'
              : 'Click "Start Export" to begin exporting all results based on current filters.'}
          </Alert>
        )}

        {status === 'running' && (
          <Alert variant='warning' className='mt-3 mb-0'>
            {isAr
              ? 'يمكنك إلغاء التصدير في أي وقت، سيتم إيقاف الطلبات فوراً.'
              : 'You can cancel the export at any time; requests will be aborted immediately.'}
          </Alert>
        )}

        {status === 'done' && (
          <Alert variant='success' className='mt-3 mb-0'>
            <i className="bi bi-file-earmark-excel-fill me-2" style={{ fontSize: '18px' }}></i>
            {isAr ? 'تم حفظ الملف على جهازك بنجاح.' : 'The file has been successfully saved to your device.'}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer className='justify-content-between'>
        {isRunning ? (
          <Button variant='danger' onClick={cancelExport}>
            {isAr ? 'إلغاء التصدير' : 'Cancel Export'}
          </Button>
        ) : (
          <Button variant='secondary' onClick={safeClose}>
            {isAr ? 'إغلاق' : 'Close'}
          </Button>
        )}

        <Button
          variant='theme'
          onClick={isRunning ? undefined : startExport}
          disabled={isRunning || status === 'done'}
          className='px-5 d-flex align-items-center gap-2'
        >
          <i className="bi bi-file-earmark-excel-fill" style={{ fontSize: '20px' }}></i>
          {isRunning && <Spinner animation='border' size='sm' className='me-2' />}
          {status === 'idle' && (isAr ? 'ابدأ التصدير' : 'Start Export')}
          {(status === 'error' || status === 'canceled') && (isAr ? 'إعادة المحاولة' : 'Retry')}
          {status === 'done' && (isAr ? 'تم التصدير' : 'Export Done')}
          {status === 'running' && (isAr ? 'جاري التصدير...' : 'Exporting...')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReportExportExcelModal;